import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getParameter, getParameterHistory } from '../services/parameterService';
import type { Parameter, ParameterHistoryEntry } from '../types';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

function getChangeTypeBadgeClass(changeType: string): string {
  switch (changeType) {
    case 'CREATED':
      return 'change-badge change-badge-created';
    case 'UPDATED':
      return 'change-badge change-badge-updated';
    default:
      return 'change-badge';
  }
}

function isFieldChanged(entry: ParameterHistoryEntry, field: string): boolean {
  if (!entry.previousValues) return false;
  const prev = entry.previousValues[field];
  if (prev === undefined) return false;
  switch (field) {
    case 'parameter_name': return prev !== entry.parameterName;
    case 'parameter_type': return prev !== entry.parameterType;
    case 'data_type': return prev !== entry.dataType;
    case 'natural_language_formula': return prev !== entry.naturalLanguageFormula;
    case 'executable_formula': return prev !== entry.executableFormula;
    case 'display_order': return prev !== entry.displayOrder;
    case 'is_active': return prev !== entry.isActive;
    default: return false;
  }
}

export default function ParameterHistoryPage() {
  const { parameterId } = useParams<{ parameterId: string }>();
  const navigate = useNavigate();

  const [parameter, setParameter] = useState<Parameter | null>(null);
  const [history, setHistory] = useState<ParameterHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!parameterId) return;

    async function fetchData() {
      setIsLoading(true);
      setError(null);
      try {
        const [paramData, historyData] = await Promise.all([
          getParameter(Number(parameterId)),
          getParameterHistory(Number(parameterId)),
        ]);
        setParameter(paramData);
        setHistory(historyData);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load parameter history';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [parameterId]);

  return (
    <div className="parameter-history-page">
      <div className="page-header">
        <h2>{parameter ? `History: ${parameter.parameterName}` : 'Parameter History'}</h2>
        <Button variant="secondary" onClick={() => navigate('/parameters')}>
          Back to Parameters
        </Button>
      </div>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingSpinner message="Loading history..." />
      ) : history.length === 0 ? (
        <p className="history-empty">No history entries found.</p>
      ) : (
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th scope="col">Change Type</th>
              <th scope="col">Parameter Name</th>
              <th scope="col">Type</th>
              <th scope="col">Data Type</th>
              <th scope="col">NL Formula</th>
              <th scope="col">Executable Formula</th>
              <th scope="col">Changed By</th>
              <th scope="col">Changed At</th>
            </tr>
          </thead>
          <tbody>
            {history.map((entry) => (
              <tr key={entry.historyId}>
                <td>
                  <span className={getChangeTypeBadgeClass(entry.changeType)}>
                    {entry.changeType}
                  </span>
                </td>
                <td className={isFieldChanged(entry, 'parameter_name') ? 'field-changed' : ''}>
                  {entry.parameterName}
                  {isFieldChanged(entry, 'parameter_name') && (
                    <span className="prev-value">
                      was: {String(entry.previousValues?.parameter_name ?? '')}
                    </span>
                  )}
                </td>
                <td>{entry.parameterType}</td>
                <td className={isFieldChanged(entry, 'data_type') ? 'field-changed' : ''}>
                  {entry.dataType || '—'}
                  {isFieldChanged(entry, 'data_type') && (
                    <span className="prev-value">
                      was: {String(entry.previousValues?.data_type ?? '—')}
                    </span>
                  )}
                </td>
                <td className={isFieldChanged(entry, 'natural_language_formula') ? 'field-changed' : ''}>
                  {entry.naturalLanguageFormula ? (
                    <span className="formula-cell" title={entry.naturalLanguageFormula}>
                      {entry.naturalLanguageFormula.length > 50
                        ? entry.naturalLanguageFormula.substring(0, 50) + '...'
                        : entry.naturalLanguageFormula}
                    </span>
                  ) : '—'}
                  {isFieldChanged(entry, 'natural_language_formula') && (
                    <span className="prev-value">
                      was: {String(entry.previousValues?.natural_language_formula ?? '—')}
                    </span>
                  )}
                </td>
                <td className={isFieldChanged(entry, 'executable_formula') ? 'field-changed' : ''}>
                  {entry.executableFormula ? (
                    <code className="formula-cell" title={entry.executableFormula}>
                      {entry.executableFormula.length > 50
                        ? entry.executableFormula.substring(0, 50) + '...'
                        : entry.executableFormula}
                    </code>
                  ) : '—'}
                  {isFieldChanged(entry, 'executable_formula') && (
                    <span className="prev-value">
                      was: {String(entry.previousValues?.executable_formula ?? '—')}
                    </span>
                  )}
                </td>
                <td>{entry.changedBy}</td>
                <td>{formatDate(entry.changedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
